<template>
  <div class="d-flex flex-column">
    <div class="grid-header">
      <h1></h1>
      <div class="toolbar">
      </div>
    </div>

    <DxDataGrid v-bind="getDefaultGridProperties()" :data-source="gridSource" ref="grid" class="dx-card wide-card grid-facturas">
      <DxColumnFixing :enabled="true" />
      <DxColumn data-field="TieneTicketSoporteAbierto" :caption="$t('facturas.grid.ticket_soporte_abierto')" data-type="boolean"
        cell-template="booleanTemplate" width="160px"/>
      <DxColumn
        data-field="TipoFactura"
        :caption="$t('facturas.grid.tipo_factura')"
        cell-template="tipoFacturaTemplate"
        data-type="string"
        width="130px"
        :calculate-cell-value="calculateTipoFacturaValue"
        :calculate-filter-expression="calculateTipoFacturaFilterExpression"
      >
        <DxLookup v-bind="getTipoFacturaLookup()" />
      </DxColumn>
      <DxColumn data-field="IdentificadorBorrador" :caption="$t('facturas.grid.codigo_borrador')"></DxColumn>
      <DxColumn data-field="Identificador" :caption="$t('facturas.grid.codigo_identificador')"></DxColumn>
      <DxColumn data-field="FechaEmision" :caption="$t('facturas.grid.fecha_emision')" data-type="date" format="dd/MM/yyyy"></DxColumn>
      <DxColumn data-field="Estado" :caption="$t('facturas.grid.estado')" cell-template="estadoTemplate">
        <DxLookup v-bind="getLookup('facturas', 'estado')"></DxLookup>
      </DxColumn>
      <DxColumn data-field="ImporteTotal" :caption="$t('facturas.grid.importe_total')" data-type="number">
        <DxFormat v-bind="currencyFormat()"></DxFormat>
      </DxColumn>
      <DxColumn data-field="MotivoRechazo" :caption="$t('facturas.grid.motivo_rechazo')" cell-template="longTextWithTooltipTemplate"></DxColumn>
      <DxColumn data-field="Notas" :caption="$t('facturas.grid.notas')" cell-template="longTextWithTooltipTemplate"></DxColumn>

      <DxColumn type="buttons" :caption="$t('facturas.grid.acciones')" :fixed="true" fixed-position="right">
        <DxButton :hint="$t('facturas.grid.ver_detalle')" icon="fa-solid fa-magnifying-glass" cssClass="text-success"
          :on-click="(e: any) => verDetalle(e.row.data.Id)" />
      </DxColumn>

      <template #estadoTemplate="{ data }">
        <div :class="[
          `d-inline-block rounded py-1 px-2`,
          `estado-factura ${data.value}`,
        ]">
            {{ $t(`facturas.estado.${data.value}`) }}
        </div>
      </template>

      <template #tipoFacturaTemplate="{ data }">
        <div
          :class="[
            'd-inline-block rounded py-1 px-2',
            'factura-emitida-por',
            data.value === 'EMITIDA' ? 'emitida' : 'rectificativa',
          ]"
        >
          {{
            data.value === 'EMITIDA'
              ? $t('facturas.tipo_factura.emitida')
              : $t('facturas.tipo_factura.rectificativa')
          }}
        </div>
      </template>

      <template #longTextWithTooltipTemplate="{ data }">
        <LongTextWithTooltip :value="data.value"/>
      </template>

      <template #booleanTemplate="{ data }">
        <BooleanTemplate :value="data.value" />
      </template>

      <DxMasterDetail :enabled="true" template="masterDetailTemplate" />
      <template #masterDetailTemplate="{ data: factura }">
        <tramites-factura-grid :template-data="factura" />
        <div class="mt-4 mb-4"></div>
        <rappels-mensuales-factura-grid :template-data="factura" />
      </template>
    </DxDataGrid>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { DxDataGrid, DxColumn, DxColumnFixing, DxLookup, DxFormat, DxMasterDetail, DxButton } from "devextreme-vue/data-grid";
import { getDefaultGridProperties, getLookup, currencyFormat } from "@helpers/grid-helper";
import type { ColumnLookup } from "devextreme/common/grids";
import { useApi } from "@api/integration";
import type CustomStore from "devextreme/data/custom_store";
import { type DXLoadOptions } from "@/.shared/.api";
import TramitesFacturaGrid from "@/components/mis-datos/TramitesFacturaGrid.vue";
import RappelsMensualesFacturaGrid from "@/components/mis-datos/RappelsMensualesFacturaGrid.vue";
import { useModalService } from "@/.shared/services/modal-service";
import { useIntl } from "@/.shared/services/i18n";
import FacturaModalDetail from "@components/facturas/FacturaModalDetail.vue";
import LongTextWithTooltip from "@components/LongTextWithTooltip.vue";
import BooleanTemplate from "@components/BooleanTemplate.vue";

const api = useApi();
const grid = ref<DxDataGrid>();
const gridSource = ref<CustomStore>();
const intl = useIntl();
const modalService = useModalService();

onMounted(async () => {
  setDataSource();
});

function getTipoFacturaLookup(): ColumnLookup {
  return {
    dataSource: [
      { value: "EMITIDA", text: intl.i18n.t("facturas.tipo_factura.emitida") },
      { value: "RECTIFICATIVA", text: intl.i18n.t("facturas.tipo_factura.rectificativa") },
    ],
    valueExpr: "value",
    displayExpr: "text",
  };
}

function calculateTipoFacturaValue(rowData: { ImporteTotal: number }): string {
  return rowData.ImporteTotal >= 0 ? "EMITIDA" : "RECTIFICATIVA";
}

function calculateTipoFacturaFilterExpression(filterValue: string | null): unknown {
  if (filterValue === "EMITIDA") {
    return ["ImporteTotal", ">=", 0];
  }

  if (filterValue === "RECTIFICATIVA") {
    return ["ImporteTotal", "<", 0];
  }

  return null;
}

const setDataSource = async () => {

  gridSource.value = api.apiStore({
    key: "Id",
    load: async (p) => {
      const q = p as DXLoadOptions;
      return await api.callStandardApi("getFacturasMyCompanyForGrid", { showLoading: false }, q)
    }
  });
}

async function verDetalle(id: string): Promise<void> {
  await modalService.showModal(FacturaModalDetail, { idFactura: id }, { });
}
</script>

<style lang="scss" scoped>
@import "@styles/tags.scss";

.factura-emitida-por {
  color: var(--tag-text);
  background-color: var(--tag-background) !important;
  border: 1px solid var(--tag-border) !important;

  &.emitida {
    @include ui-tag(azul);
  }

  &.rectificativa {
    @include ui-tag(secondary);
  }
}
</style>
